import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { IngestMode } from '~/components/common/enrichment-run-panel';
import type { AlreadyCuratedPreflightSnapshot } from '~/features/already-curated/types/already-curated.types';
import type { AppliedProductFilters } from '~/features/experiments/types/product-metadata.types';
import type { ProductGroupsChoice } from '~/features/product-groups/types/product-groups.types';

import type { EnrichmentRunConfig } from '../types/enrichment-run-config';
import type { EnrichmentJobStatus } from '../types/scheduled-enrichment.types';

/** The create screen's "Not enriched yet" choice (docs/not-enriched-carryover-plan.md). */
export interface CarryoverChoice {
  /** The subsidiary the choice was made for; a choice for another one is never sent. */
  subsidiaryId: string;
  /** The batch it was made for (set at create); a resumed other batch never sends it. */
  jobId?: string;
  include: boolean;
  includeFailedPermanent: boolean;
  includeExcluded: boolean;
}

/**
 * Persisted state for a single scheduled-enrichment session (Meta or Google).
 * Workflow IDs survive page refresh — pipelines run 30+ minutes so the user
 * must be able to close and reopen the tab mid-run. `reset()` clears stale IDs
 * before a fresh run.
 */
export interface ScheduledEnrichmentState {
  /** ID of the scheduled job created by `POST enrichments/scheduled/jobs`. */
  scheduledJobId: string | null;
  /** Workflow ID returned by the content-scoring trigger; drives loading-screen polling. */
  contentScoringWorkflowId: string | null;
  /** Workflow ID returned by the sample-enrichment trigger; drives sample-progress polling. */
  sampleEnrichmentWorkflowId: string | null;
  /** Workflow ID returned by the pipeline trigger; drives full-catalog polling. */
  pipelineWorkflowId: string | null;
  /** Sync snapshot ID passed to every trigger payload. */
  syncId: string | null;
  /** Catalog connector's connectionId used on the product-selection page. */
  connectionId: string | null;
  /** Catalog-attribute filters applied on the product-selection page; passed to all triggers. */
  filters: AppliedProductFilters | null;
  /**
   * The run configuration the operator chose in the enrichment-run panel —
   * attributes, descriptor steps and scoring — so every later trigger in the chain
   * enriches the same set with the same steps.
   *
   * Persisted for the same reason the workflow IDs are: the pipeline is triggered
   * from a page reached long after the panel closed, potentially after a reload.
   * `null` means "not chosen in this browser" and spreads to nothing, letting the
   * server default apply — distinct from an explicit all-off choice.
   *
   * One field rather than a flag set plus an attribute list: it is one decision the
   * operator makes once, and splitting it invites a trigger that carries half of it.
   */
  runConfig: EnrichmentRunConfig | null;
  /**
   * Whether the operator asked to bypass the enrichment cache and regenerate
   * fresh. Persisted alongside {@link runConfig} for the same reason: the sample
   * and full-catalog triggers are fired from later pages and must carry the
   * operator's choice. Defaults to `false`.
   */
  overrideCache: boolean;
  /**
   * Curation ingest scope chosen in the run panel. Persisted for the same reason
   * as {@link runConfig}: the sample and full-catalog triggers fire from later
   * pages, and a run whose second half used a different scope than its first is
   * not the run the operator configured.
   *
   * `null` = the surface never offered the control; the key is then omitted from
   * every trigger so the server default decides.
   */
  ingestMode: IngestMode | null;
  /**
   * "Not enriched yet" carry-over: what the operator chose for products earlier
   * batches curated but never enriched. Sent only on the full-catalog trigger,
   * which fires from a later page — hence persisted. `null` = the screen never
   * offered it (no such products, or a resumed older session): the key is omitted.
   */
  carryover: CarryoverChoice | null;
  /**
   * The product groups the operator included (curation classes and "not enriched
   * before" buckets, each opt-in). Sent on the full-catalog trigger, which fires
   * from a later page — hence persisted. `null` = the screen never offered them:
   * the keys are omitted and the server's defaults decide.
   */
  productGroups: ProductGroupsChoice | null;
  /**
   * The pre-flight already-curated breakdown (③) captured at run-submit time.
   * The post-run timeline + Enrichment Overview render a faithful "already
   * curated (skipped)" summary from this, because the run-scoped endpoint (②)
   * cannot reconcile whole-sync runs (it 400s) and a post-run ③ over-counts.
   * Persisted for the same reason as {@link runConfig}: the surfaces that read
   * it are reached long after the panel closed, possibly after a reload. `null`
   * = no breakdown captured (the job was still running when the run started, or
   * an older session predating this field).
   */
  preflightSnapshot: AlreadyCuratedPreflightSnapshot | null;
  /**
   * Known statuses from the job-status API snapshot, written during batch resume.
   * The adapter reads these as a fallback before the first polling response arrives,
   * preventing the layout from flashing the enrichment progress view on resume.
   */
  contentScoringInitialStatus: EnrichmentJobStatus | null;
  sampleEnrichmentInitialStatus: EnrichmentJobStatus | null;
  pipelineInitialStatus: EnrichmentJobStatus | null;
  /**
   * The workflow id each stage replaced when this browser last (re)triggered it.
   *
   * `GET /jobs/{id}/status` keeps returning the OLD workflow for a stage for a
   * short window after a re-trigger, so the snapshot alone can point the funnel
   * at a dead run — typically a terminal `FAILED` one, which is then reported as
   * the status of the run that just started. Recording what was superseded lets
   * the adapter recognise and skip exactly that stale answer, and nothing else:
   * a workflow id this browser never superseded is always taken from the server.
   * Self-clearing — once the backend returns the new id, the snapshot stops
   * matching and normal precedence resumes.
   *
   * Persisted because it must survive the reload that happens mid-run; scoped by
   * {@link scheduledJobId} so it can never suppress another job's workflow.
   */
  contentScoringSupersededWorkflowId: string | null;
  sampleEnrichmentSupersededWorkflowId: string | null;
  pipelineSupersededWorkflowId: string | null;

  setScheduledJobId: (id: string) => void;
  /**
   * Points a stage at a newly triggered workflow. `supersedes` is the workflow
   * the funnel was watching before this trigger — pass it so a lagging job-status
   * snapshot echoing that id can be identified as stale.
   */
  setContentScoringWorkflowId: (id: string, supersedes?: string | null) => void;
  setSampleEnrichmentWorkflowId: (id: string, supersedes?: string | null) => void;
  setPipelineWorkflowId: (id: string, supersedes?: string | null) => void;
  setSyncId: (id: string) => void;
  setConnectionId: (id: string) => void;
  setFilters: (filters: AppliedProductFilters) => void;
  setRunConfig: (config: EnrichmentRunConfig) => void;
  setOverrideCache: (value: boolean) => void;
  setIngestMode: (mode: IngestMode | null) => void;
  setCarryover: (choice: CarryoverChoice | null) => void;
  setProductGroups: (choice: ProductGroupsChoice | null) => void;
  /** Records the pre-flight already-curated breakdown for the run being started. */
  setPreflightSnapshot: (snapshot: AlreadyCuratedPreflightSnapshot | null) => void;
  /**
   * Atomically restores all workflow IDs and their known statuses from a
   * job-status API response. Used when resuming an in-progress batch from the
   * catalog-operations list — a single `set()` avoids multiple sequential
   * re-renders and localStorage writes.
   */
  hydrate: (data: {
    scheduledJobId: string;
    contentScoringWorkflowId: string | null;
    sampleEnrichmentWorkflowId: string | null;
    pipelineWorkflowId: string | null;
    syncId: string | null;
    filters: AppliedProductFilters | null;
    connectionId: string | null;
    /** Omitted by the resume path — the job-status API doesn't echo the run
     *  configuration, so resume must not clobber whatever this browser already had. */
    runConfig?: EnrichmentRunConfig | null;
    contentScoringInitialStatus: EnrichmentJobStatus | null;
    sampleEnrichmentInitialStatus: EnrichmentJobStatus | null;
    pipelineInitialStatus: EnrichmentJobStatus | null;
  }) => void;
  /** Clears all IDs — call before starting a new enrichment run. */
  reset: () => void;
}

const INITIAL_STATE = {
  scheduledJobId: null,
  contentScoringWorkflowId: null,
  sampleEnrichmentWorkflowId: null,
  pipelineWorkflowId: null,
  syncId: null,
  connectionId: null,
  filters: null,
  runConfig: null,
  overrideCache: false,
  ingestMode: null,
  carryover: null,
  productGroups: null,
  preflightSnapshot: null,
  contentScoringInitialStatus: null,
  sampleEnrichmentInitialStatus: null,
  pipelineInitialStatus: null,
  contentScoringSupersededWorkflowId: null,
  sampleEnrichmentSupersededWorkflowId: null,
  pipelineSupersededWorkflowId: null,
};

/**
 * Reconciles one stage of a job-status snapshot against what this browser knows.
 *
 * A snapshot that still names the workflow we superseded is lagging behind a
 * re-trigger, so the locally triggered id is kept (and the record with it, since
 * the backend hasn't caught up yet). Any other id — including one this browser
 * has never seen, e.g. a run started from another tab — is taken from the server,
 * which also clears the now-obsolete record.
 */
function resolveHydratedStage(
  incomingWorkflowId: string | null,
  incomingStatus: EnrichmentJobStatus | null,
  localWorkflowId: string | null,
  supersededWorkflowId: string | null,
): {
  workflowId: string | null;
  initialStatus: EnrichmentJobStatus | null;
  superseded: string | null;
} {
  const snapshotIsLagging =
    incomingWorkflowId != null &&
    incomingWorkflowId === supersededWorkflowId &&
    localWorkflowId != null;
  if (snapshotIsLagging) {
    // No status: the snapshot describes the dead run, so nothing here speaks for
    // the workflow we keep watching until its own poll answers.
    return { workflowId: localWorkflowId, initialStatus: null, superseded: supersededWorkflowId };
  }
  return { workflowId: incomingWorkflowId, initialStatus: incomingStatus, superseded: null };
}

/**
 * Factory building a persisted Zustand store for one scheduled-enrichment
 * platform. Each platform gets a distinct localStorage `persistName` so the
 * Meta and Google sessions never cross-contaminate.
 */
export function createScheduledEnrichmentStore(persistName: string) {
  return create<ScheduledEnrichmentState>()(
    persist(
      (set) => ({
        ...INITIAL_STATE,
        setScheduledJobId: (id) => set({ scheduledJobId: id }),
        // Each `*InitialStatus` describes the workflow id it was hydrated
        // alongside, so pointing a stage at a NEW workflow must discard it —
        // otherwise a previous run's terminal status (e.g. FAILED) is read as
        // the status of the run that just started.
        setContentScoringWorkflowId: (id, supersedes = null) =>
          set({
            contentScoringWorkflowId: id,
            contentScoringInitialStatus: null,
            contentScoringSupersededWorkflowId: supersedes === id ? null : supersedes,
          }),
        setSampleEnrichmentWorkflowId: (id, supersedes = null) =>
          set({
            sampleEnrichmentWorkflowId: id,
            sampleEnrichmentInitialStatus: null,
            sampleEnrichmentSupersededWorkflowId: supersedes === id ? null : supersedes,
          }),
        setPipelineWorkflowId: (id, supersedes = null) =>
          set({
            pipelineWorkflowId: id,
            pipelineInitialStatus: null,
            pipelineSupersededWorkflowId: supersedes === id ? null : supersedes,
          }),
        setSyncId: (id) => set({ syncId: id }),
        setConnectionId: (id) => set({ connectionId: id }),
        setFilters: (f) => set({ filters: f }),
        setRunConfig: (config) => set({ runConfig: config }),
        setOverrideCache: (value) => set({ overrideCache: value }),
        setIngestMode: (mode) => set({ ingestMode: mode }),
        setCarryover: (choice) => set({ carryover: choice }),
        setProductGroups: (choice) => set({ productGroups: choice }),
        setPreflightSnapshot: (snapshot) => set({ preflightSnapshot: snapshot }),
        // `runConfig` is spread only when the caller supplied it, so a resume that
        // can't know it leaves the persisted choice intact rather than overwriting
        // it with undefined.
        hydrate: ({ runConfig, ...rest }) =>
          set((state) => {
            // The superseded records only speak for the job they were written
            // under; resuming a different job discards them wholesale.
            const sameJob = state.scheduledJobId === rest.scheduledJobId;
            const contentScoring = resolveHydratedStage(
              rest.contentScoringWorkflowId,
              rest.contentScoringInitialStatus,
              state.contentScoringWorkflowId,
              sameJob ? state.contentScoringSupersededWorkflowId : null,
            );
            const sample = resolveHydratedStage(
              rest.sampleEnrichmentWorkflowId,
              rest.sampleEnrichmentInitialStatus,
              state.sampleEnrichmentWorkflowId,
              sameJob ? state.sampleEnrichmentSupersededWorkflowId : null,
            );
            const pipeline = resolveHydratedStage(
              rest.pipelineWorkflowId,
              rest.pipelineInitialStatus,
              state.pipelineWorkflowId,
              sameJob ? state.pipelineSupersededWorkflowId : null,
            );
            return {
              ...rest,
              ...(runConfig === undefined ? {} : { runConfig }),
              contentScoringWorkflowId: contentScoring.workflowId,
              contentScoringInitialStatus: contentScoring.initialStatus,
              contentScoringSupersededWorkflowId: contentScoring.superseded,
              sampleEnrichmentWorkflowId: sample.workflowId,
              sampleEnrichmentInitialStatus: sample.initialStatus,
              sampleEnrichmentSupersededWorkflowId: sample.superseded,
              pipelineWorkflowId: pipeline.workflowId,
              pipelineInitialStatus: pipeline.initialStatus,
              pipelineSupersededWorkflowId: pipeline.superseded,
            };
          }),
        reset: () => set(INITIAL_STATE),
      }),
      {
        name: persistName,
        storage: createJSONStorage(() => localStorage),
        // 6 replaces the flat `postStepFlags` + `contentAttributeKeys` pair with the
        // nested `runConfig` the scheduled routes now take. Sessions persisted under
        // 5 or earlier rehydrate with `runConfig` absent, which reads as "no choice
        // recorded" — the later triggers fall back to the server default rather than
        // sending a half-migrated body.
        //
        // NB: `preflightSnapshot`, `ingestMode` and `productGroups` were added WITHOUT a version
        // bump — each is a new optional nullable field, so zustand's default shallow
        // merge fills it from INITIAL_STATE (null) for older v6 sessions. Bumping
        // would needlessly discard in-progress runs (losing the persisted workflow
        // IDs mid-run). A null `ingestMode` on a resumed session simply omits the key,
        // which is the same "server decides" state a pre-upgrade run already had.
        version: 6,
      },
    ),
  );
}

/** Zustand store hook type produced by {@link createScheduledEnrichmentStore}. */
export type ScheduledEnrichmentStore = ReturnType<typeof createScheduledEnrichmentStore>;
